import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { drawingsApi, type Drawing } from '@/api/endpoints';
import { Alert, Button } from '@/components/ui';
import { SavedFileNotice } from '@/features/exports/SavedFileNotice';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { isNative, saveBlob, saveFileErrorCode, type SavedFile } from '@/native';

/**
 * 下载一张图纸 / 进度表的原文件（CR-043）。
 *
 * 文件不经过 API 进程：先问服务端要一个 5 分钟有效、响应带 `attachment` 的签名地址，
 * 浏览器直接打开就是另存为；App 里 `<a download>` 失效，改成 fetch 回来交给 `saveBlob`
 * —— 存进手机的「下载」文件夹，下面出「打开」「分享」（CR-054，图纸 PDF 正是要打印的那种）。
 * 权限由放不放这个按钮的页面判（`drawing.export` / `schedule.export`）。
 */
export function DrawingDownload({ drawing }: { drawing: Drawing }) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedFile | null>(null);

  async function download() {
    setError(null);
    setSaved(null);
    setBusy(true);
    try {
      const { url, filename } = await drawingsApi.downloadUrl(drawing.id);
      if (isNative) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setSaved(await saveBlob(await response.blob(), filename));
      } else {
        // 对象存储的响应带 Content-Disposition: attachment，跨域链接也会另存为
        const link = document.createElement('a');
        link.href = url;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      setError(saveFileErrorCode(err) === 'PERMISSION_DENIED' ? t('export.storageDenied') : toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="ghost"
        loading={busy}
        onClick={() => void download()}
        data-action={drawing.kind === 'SCHEDULE' ? 'schedule.export' : 'drawing.export'}
      >
        {t('export.download')}
      </Button>
      {error && <Alert>{error}</Alert>}
      {saved && <SavedFileNotice file={saved} />}
    </div>
  );
}
